/**
 * RealtimeClient — the single WebSocket client for every messengerapp surface.
 *
 * Replaces three near-identical implementations:
 *   - apps/widget/src/lib/realtime/ws.ts          (operator, 251 lines)
 *   - apps/widget-customer/src/lib/realtime/ws.ts (visitor, 305 lines)
 *   - apps/admin-console/src/app/inbox/realtime-client.ts (operator, 162 lines)
 *
 * The operator/visitor distinction was never a behavioural difference — both
 * speak the same protocol against the same gateway. The only thing that differs
 * is which endpoint mints the ticket, which is why that arrives as a
 * `ticketProvider` callback rather than a subclass.
 *
 * Deliberately framework-free: no Svelte runes, no Angular signals, no
 * observables. Each app wraps this in whatever reactivity it uses, so the
 * shared protocol code stays shared even if the apps never converge on one
 * framework.
 *
 * ## Divergence this consolidation fixes
 *
 * `admin-console` was missing the visibility-recovery logic the two widgets
 * have. A backgrounded tab can have its socket reaped by the OS while
 * `readyState` still reports OPEN, so an operator returning to the tab held a
 * dead connection until their next action timed out. That is now impossible to
 * have in one surface and not another.
 */
const DEFAULT_BASE_RECONNECT_MS = 1_000;
const DEFAULT_MAX_RECONNECT_MS = 30_000;
const DEFAULT_VISIBILITY_RECONNECT_MS = 90_000;
export class RealtimeClient {
    constructor(options) {
        this.socket = null;
        this.handlers = new Set();
        this.openHandlers = new Set();
        this.closeHandlers = new Set();
        this.errorHandlers = new Set();
        this.reconnectAttempt = 0;
        this.reconnectTimer = null;
        this.explicitClose = false;
        this.connecting = false;
        this.lastActivityMs = 0;
        this.visibilityHandler = null;
        this.opts = options;
        this.WebSocketCtor =
            options.webSocketImpl ??
                globalThis.WebSocket;
        // bind(globalThis): these are stored as instance fields and invoked as
        // `this.setTimeoutImpl(...)`. Unbound, browsers dispatch them with
        // `this` = the client and throw "Illegal invocation" — which silently
        // killed every reconnect in production. Node tolerates the unbound
        // call, so unit tests never saw it.
        this.setTimeoutImpl =
            options.setTimeoutImpl ?? globalThis.setTimeout.bind(globalThis);
        this.clearTimeoutImpl =
            options.clearTimeoutImpl ?? globalThis.clearTimeout.bind(globalThis);
        this.baseReconnectMs = options.baseReconnectMs ?? DEFAULT_BASE_RECONNECT_MS;
        this.maxReconnectMs = options.maxReconnectMs ?? DEFAULT_MAX_RECONNECT_MS;
        this.visibilityReconnectMs =
            options.visibilityReconnectMs ?? DEFAULT_VISIBILITY_RECONNECT_MS;
    }
    /** Subscribe to inbound frames. Returns an unsubscribe fn. */
    on(handler) {
        this.handlers.add(handler);
        return () => this.handlers.delete(handler);
    }
    /**
     * Fires on every successful open, including automatic reconnects.
     *
     * Presence depends on this: a reconnect gives the gateway a new socket with
     * no memory of what the operator was viewing, so `viewing.start` has to be
     * re-announced or the server-side tracker silently loses them.
     */
    onOpen(handler) {
        this.openHandlers.add(handler);
        return () => this.openHandlers.delete(handler);
    }
    /** Fires on every close, including an explicit `close()`. */
    onClose(handler) {
        this.closeHandlers.add(handler);
        return () => this.closeHandlers.delete(handler);
    }
    /** Fires when ticket-minting throws. Reconnect still proceeds unless closed. */
    onError(handler) {
        this.errorHandlers.add(handler);
        return () => this.errorHandlers.delete(handler);
    }
    /** True while a socket is open. */
    get connected() {
        return this.socket !== null && this.socket.readyState === this.openState;
    }
    get openState() {
        return this.WebSocketCtor.OPEN ?? 1;
    }
    /**
     * Open the connection. Idempotent — a second call while a socket exists or a
     * ticket mint is in flight is a no-op, so callers may invoke it freely on
     * mount without guarding.
     */
    async connect() {
        if (this.socket || this.connecting)
            return;
        this.explicitClose = false;
        this.connecting = true;
        try {
            const ticket = await this.opts.ticketProvider();
            const socket = new this.WebSocketCtor(this.buildUrl(ticket));
            this.socket = socket;
            socket.addEventListener('open', () => {
                this.reconnectAttempt = 0;
                this.lastActivityMs = Date.now();
                this.installVisibilityHandler();
                this.emit(this.openHandlers);
            });
            socket.addEventListener('message', (ev) => {
                // Any inbound frame is a liveness signal — the browser does not expose
                // transport-level pings to JS, so frames are all we can observe.
                this.lastActivityMs = Date.now();
                const frame = parseFrame(ev.data);
                if (!frame)
                    return;
                for (const h of this.handlers) {
                    try {
                        h(frame);
                    }
                    catch {
                        // A handler crash must not break the socket loop.
                    }
                }
            });
            socket.addEventListener('close', (ev) => {
                // 1000 normal · 1001 going-away · 1006 abnormal (no close frame —
                // almost always network/proxy/tab-throttle) · 4000 our own force.
                this.opts.logger?.('close', {
                    code: ev.code,
                    reason: ev.reason || null,
                    wasClean: ev.wasClean,
                    explicit: this.explicitClose,
                });
                this.socket = null;
                this.emit(this.closeHandlers);
                if (!this.explicitClose)
                    this.scheduleReconnect();
            });
            socket.addEventListener('error', () => {
                // Browsers follow 'error' with 'close'; the reconnect path lives there.
            });
        }
        catch (err) {
            const error = err instanceof Error ? err : new Error(String(err));
            for (const h of this.errorHandlers) {
                try {
                    h(error);
                }
                catch {
                    // An error-handler crash must not prevent reconnect bookkeeping.
                }
            }
            if (!this.explicitClose)
                this.scheduleReconnect();
        }
        finally {
            this.connecting = false;
        }
    }
    /**
     * Send a JSON frame. No-op unless the socket is OPEN.
     *
     * Best-effort by design: `viewing.start`/`stop` and typing frames are
     * recoverable via the onOpen resume path or the next user action, so a
     * dropped one must never surface as an error to the operator.
     */
    send(payload) {
        const s = this.socket;
        if (!s || s.readyState !== this.openState)
            return;
        try {
            s.send(JSON.stringify(payload));
        }
        catch {
            // See above — deliberately swallowed.
        }
    }
    /** Explicit close. Disables auto-reconnect for the lifetime of this client. */
    close() {
        this.explicitClose = true;
        if (this.reconnectTimer !== null) {
            this.clearTimeoutImpl(this.reconnectTimer);
            this.reconnectTimer = null;
        }
        this.removeVisibilityHandler();
        const s = this.socket;
        this.socket = null;
        if (s) {
            try {
                s.close();
            }
            catch {
                // ignore
            }
        }
    }
    emit(set) {
        for (const h of set) {
            try {
                h();
            }
            catch {
                // A subscriber crash must not break the socket loop.
            }
        }
    }
    /**
     * Force-close and reschedule. Used when we suspect a zombie connection: the
     * server may already have dropped us while `readyState` still says OPEN, so
     * closing explicitly is what kicks us into the normal reconnect path.
     */
    forceReconnect(reason) {
        if (this.explicitClose)
            return;
        this.opts.logger?.('force-reconnect', { reason });
        const s = this.socket;
        this.socket = null;
        if (s) {
            try {
                s.close(4000, reason);
            }
            catch {
                // ignore
            }
        }
        if (this.reconnectTimer === null)
            this.scheduleReconnect();
    }
    installVisibilityHandler() {
        if (this.visibilityHandler)
            return;
        if (this.visibilityReconnectMs <= 0)
            return;
        if (typeof document === 'undefined')
            return;
        this.visibilityHandler = () => {
            if (document.visibilityState !== 'visible')
                return;
            if (this.lastActivityMs === 0)
                return;
            // Only after a meaningful idle gap — reconnecting on every tab switch
            // would churn the socket for no reason.
            if (Date.now() - this.lastActivityMs > this.visibilityReconnectMs) {
                this.forceReconnect('visibility regain after silence');
            }
        };
        document.addEventListener('visibilitychange', this.visibilityHandler);
    }
    removeVisibilityHandler() {
        if (!this.visibilityHandler)
            return;
        if (typeof document !== 'undefined') {
            document.removeEventListener('visibilitychange', this.visibilityHandler);
        }
        this.visibilityHandler = null;
    }
    buildUrl(ticket) {
        const base = ticket.wsUrl || this.opts.defaultWsUrl;
        if (!base)
            throw new Error('RealtimeClient: no wsUrl on the ticket and no defaultWsUrl set');
        const sep = base.includes('?') ? '&' : '?';
        return `${base}${sep}ticket=${encodeURIComponent(ticket.token)}`;
    }
    scheduleReconnect() {
        if (this.explicitClose)
            return;
        if (this.reconnectTimer !== null)
            return;
        const delay = Math.min(this.maxReconnectMs, this.baseReconnectMs * Math.pow(2, this.reconnectAttempt));
        this.reconnectAttempt += 1;
        this.reconnectTimer = this.setTimeoutImpl(() => {
            this.reconnectTimer = null;
            void this.connect();
        }, delay);
    }
}
/** Exported for tests; malformed frames are dropped rather than thrown on. */
export function parseFrame(data) {
    if (typeof data !== 'string')
        return null;
    let parsed;
    try {
        parsed = JSON.parse(data);
    }
    catch {
        return null;
    }
    if (!parsed || typeof parsed !== 'object')
        return null;
    if (typeof parsed.eventName !== 'string')
        return null;
    return parsed;
}
//# sourceMappingURL=realtime-client.js.map